import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Users, Settings, ClipboardList, Package,
  Wrench, HardHat, Building2, Calculator,
  FolderKanban, Truck, CreditCard, Receipt,
  ShieldCheck, FileText, LayoutDashboard,
  ChevronDown, ChevronRight, Layers, BookOpen,
  BarChart2, Hammer, AlertTriangle, DollarSign,
  FileCheck, Landmark
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

interface MenuItem {
  label: string;
  path?: string;
  icon: React.ReactNode;
  built: boolean;
  roles?: string[];
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  { label:'Dashboard',         path:'/dashboard',     icon:<LayoutDashboard size={18}/>, built:true },

  // ── User Management ────────────────────────────────────────
  {
    label:'User Management', icon:<Users size={18}/>, built:true, roles:['ADMIN','MD'],
    children:[
      { label:'All Users',   path:'/users',        icon:<Users size={16}/>,         built:true },
      { label:'Create User', path:'/users/create', icon:<Users size={16}/>,         built:true },
      { label:'Roles',       path:'/roles',        icon:<ShieldCheck size={16}/>,   built:true },
      { label:'Audit Log',   path:'/audit',        icon:<ClipboardList size={16}/>, built:true },
    ],
  },

  // ── Masters ────────────────────────────────────────────────
  { label:'Materials',          path:'/materials',    icon:<Package size={18}/>,       built:true },
  { label:'Machinery',          path:'/machinery',    icon:<Wrench size={18}/>,        built:true },
  { label:'Tools',              path:'/tools',        icon:<Settings size={18}/>,      built:true },
  { label:'HR & Employees',     path:'/hr',           icon:<HardHat size={18}/>,       built:true },
  { label:'Activity Master',    path:'/activities',   icon:<ClipboardList size={18}/>, built:true },

  // ── Sales & Estimation ─────────────────────────────────────
  {
    label:'Sales & Estimation', icon:<Calculator size={18}/>, built:true,
    children:[
      { label:'Leads & Customers', path:'/leads',         icon:<Building2 size={16}/>,    built:true },
      { label:'Smart Estimator',   path:'/estimation',    icon:<Calculator size={16}/>,   built:true },
      { label:'Material Spec',     path:'/material-spec', icon:<Layers size={16}/>,       built:true },
    ],
  },

  // ── Projects ───────────────────────────────────────────────
  {
    label:'Projects', icon:<FolderKanban size={18}/>, built:true,
    children:[
      { label:'All Projects',      path:'/projects',      icon:<FolderKanban size={16}/>, built:true },
      { label:'Activity Planning', path:'/activity-plan', icon:<ClipboardList size={16}/>, built:true },
    ],
  },
  { label:'Material Indents', path:'/material-indents', icon:<Package size={18}/>,  built:true },
  { label:'Resource Plan',    path:'/resource-plan',    icon:<ClipboardList size={18}/>, built:true },
  { label:'Quotation Template', path:'/quotation-template', icon:<FileText size={18}/>, built:true },
  { label:'Gantt Chart',      path:'/gantt',            icon:<BarChart2 size={18}/>, built:false },
  { label:'Site Diary',       path:'/site-diary',       icon:<FileText size={18}/>,  built:false },

  // ── Operations ────────────────────────────────────────────
  {
    label:'Operations', icon:<Hammer size={18}/>, built:false,
    children:[
      { label:'Procurement',    path:'/procurement',  icon:<Truck size={16}/>,         built:false },
      { label:'Site Attendance',path:'/attendance',   icon:<ClipboardList size={16}/>, built:false },
      { label:'Subcontract',    path:'/subcontract',  icon:<FileCheck size={16}/>,     built:false },
    ],
  },

  // ── Finance ────────────────────────────────────────────────
  {
    label:'Finance', icon:<DollarSign size={18}/>, built:false,
    children:[
      { label:'Payments',       path:'/payments',     icon:<CreditCard size={16}/>,    built:false },
      { label:'Billing',        path:'/billing',      icon:<Receipt size={16}/>,       built:false },
      { label:'Payroll',        path:'/payroll',      icon:<BarChart2 size={16}/>,     built:false },
    ],
  },

  // ── Quality & Safety ───────────────────────────────────────
  { label:'Quality & Safety',   path:'/quality',      icon:<ShieldCheck size={18}/>,   built:false },

  // ── Compliance ─────────────────────────────────────────────
  {
    label:'Legal & Compliance', icon:<Landmark size={18}/>, built:false, roles:['ADMIN','MD'],
    children:[
      { label:'RERA',           path:'/rera',         icon:<FileText size={16}/>,      built:false },
      { label:'Insurance',      path:'/insurance',    icon:<ShieldCheck size={16}/>,   built:false },
    ],
  },

  // ── Reports ────────────────────────────────────────────────
  { label:'Reports',            path:'/reports',      icon:<BarChart2 size={18}/>,     built:false, roles:['ADMIN','MD'] },
];

const Sidebar = () => {
  const { user, hasRole } = useAuth();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState<string[]>(['User Management','Sales & Estimation','Projects']);

  const toggleMenu = (label: string) =>
    setOpenMenus(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);

  const visibleItems = menuItems.filter(item => !item.roles || item.roles.some(r => hasRole(r)));

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors duration-150 ${
      isActive
        ? 'bg-blue-600 text-white font-medium'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`;

  return (
    <aside className="w-60 min-h-screen bg-white border-r border-gray-200 flex flex-col">
      {/* Logo */}
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Building2 size={18} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-gray-900 leading-none">RB-PMS</p>
            <p className="text-xs text-gray-400 mt-0.5">Rathna Bhoomi</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {visibleItems.map((item) => {
          if (item.children) {
            const isOpen = openMenus.includes(item.label);
            const isChildActive = item.children.some(c => c.path && location.pathname.startsWith(c.path));
            const hasBuiltChild = item.children.some(c => c.built);
            return (
              <div key={item.label}>
                <button onClick={() => toggleMenu(item.label)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors
                    ${isChildActive
                      ? 'text-blue-700 bg-blue-50 font-medium'
                      : item.built || hasBuiltChild
                        ? 'text-gray-600 hover:bg-gray-100'
                        : 'text-gray-400'}`}>
                  <span className="flex items-center gap-2.5">
                    {item.icon}{item.label}
                    {!hasBuiltChild && (
                      <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded ml-1">Soon</span>
                    )}
                  </span>
                  {isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                </button>
                {isOpen && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-100 pl-3">
                    {item.children.map(child =>
                      child.built && child.path ? (
                        <NavLink key={child.path} to={child.path} className={navLinkClass}>
                          {child.icon}{child.label}
                        </NavLink>
                      ) : (
                        <div key={child.label}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 cursor-not-allowed">
                          {child.icon}{child.label}
                          <span className="ml-auto text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">Soon</span>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          }

          return item.built && item.path ? (
            <NavLink key={item.path} to={item.path} className={navLinkClass}>
              {item.icon}{item.label}
            </NavLink>
          ) : (
            <div key={item.label}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-400 cursor-not-allowed">
              {item.icon}{item.label}
              <span className="ml-auto text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">Soon</span>
            </div>
          );
        })}
      </nav>

      {/* User */}
      <div className="px-4 py-3 border-t border-gray-200">
        <p className="text-xs font-medium text-gray-700 truncate">{user?.full_name}</p>
        <p className="text-xs text-gray-400">{user?.role_code}</p>
      </div>
    </aside>
  );
};

export default Sidebar;
